import * as React from "react";
import { useMergeRefs, FloatingPortal, FloatingOverlay, FloatingFocusManager } from "@floating-ui/react";
import clsx from "clsx";
import { tabbable } from "tabbable";
import { CloseButton } from "../../close-button/close-button.mjs";
import { Heading } from "../../typography/heading/heading.mjs";
import "debounce";
import { useIsMounted } from "../../helpers/helper/hooks/use-is-mounted.mjs";
import { Image } from "../../image/image.mjs";
import { useLabels } from "../../helpers/label-provider/use-labels.mjs";
import { Spinner } from "../../spinner/spinner.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { Video } from "../../video/video.mjs";
import { LightboxContext } from "../lightbox-provider/lightbox-provider.mjs";
const Lightbox = (props) => {
  const { className, title } = props;
  const isMounted = useIsMounted();
  const { close: onClose, id, isOpen, state } = React.useContext(LightboxContext);
  const element = React.useRef(null);
  const ref = useMergeRefs([state == null ? void 0 : state.refs.setFloating, element]);
  const { portalRoot } = useTheme();
  const close = React.useCallback(
    (event) => {
      event.preventDefault();
      onClose(event);
    },
    [onClose]
  );
  const handleMediaLoad = React.useCallback(() => {
    window.setTimeout(() => {
      if (isMounted && isOpen) {
        if (element.current) {
          const firstTabbableElement = tabbable(element.current)[0];
          if (firstTabbableElement) {
            firstTabbableElement.focus();
            return;
          }
          element.current.focus();
        }
      }
    });
  }, [isMounted, isOpen]);
  return (state == null ? void 0 : state.isMounted) && /* @__PURE__ */ React.createElement(FloatingPortal, { root: portalRoot }, /* @__PURE__ */ React.createElement(FloatingOverlay, { lockScroll: true, className: clsx("lightbox", "is-".concat(state.status), className) }, /* @__PURE__ */ React.createElement(FloatingFocusManager, { context: state.context, outsideElementsInert: true, modal: true }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: "lightbox__inner",
      ref,
      "aria-labelledby": title ? "".concat(id, "-title") : void 0,
      ...state.getFloatingProps({
        onClick: (e) => {
          e.stopPropagation();
        },
        id
      })
    },
    /* @__PURE__ */ React.createElement(LightboxContent, { ...props, id, isMounted, isOpen, onClose, onCloseClick: close, onMediaLoad: handleMediaLoad })
  ))));
};
const LightboxContent = (props) => {
  const { closeButtonLabel, id, image, title, video, onCloseClick, onMediaLoad } = props;
  const innerElement = React.useRef(null);
  const headerRef = React.useRef(null);
  const labels = useLabels();
  const [hasMediaLoaded, setHasMediaLoaded] = React.useState(false);
  const [style, setStyle] = React.useState({
    maxWidth: "100%",
    width: "100%"
  });
  const setContentStyle = React.useCallback(() => {
    var _a, _b;
    if (image) {
      if (innerElement.current) {
        const headerHeight = (_b = (_a = headerRef.current) == null ? void 0 : _a.clientHeight) != null ? _b : 0;
        const nextWidth = innerElement.current.clientWidth / (innerElement.current.clientHeight - headerHeight) * (window.innerHeight - 80 - headerHeight);
        if (image.width) {
          setStyle({
            maxWidth: image.width <= 1230 ? image.width : "100%",
            width: nextWidth
          });
        } else {
          setStyle({
            width: nextWidth
          });
        }
      }
    } else {
      setStyle({
        width: 1.77 * window.innerHeight - (title ? 170 : 80)
      });
    }
  }, [image, title]);
  const handleMediaLoad = React.useCallback(
    (event) => {
      if (video || event.currentTarget.hasAttribute("srcset")) {
        setHasMediaLoaded(true);
        onMediaLoad();
      }
      setContentStyle();
    },
    [setContentStyle, video, onMediaLoad]
  );
  React.useEffect(() => {
    const onResize = () => {
      setContentStyle();
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [setContentStyle]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: clsx("lightbox__box", { "has-media-loaded": !!hasMediaLoaded }), style, "data-theme": "dark" }, /* @__PURE__ */ React.createElement("div", { className: "lightbox__box-inner", ref: innerElement }, /* @__PURE__ */ React.createElement(CloseButton, { className: "lightbox__close", onClick: onCloseClick, label: closeButtonLabel != null ? closeButtonLabel : labels.get("close"), theme: "dark", type: "modal" }), title && /* @__PURE__ */ React.createElement("div", { className: "lightbox__header", ref: headerRef }, /* @__PURE__ */ React.createElement(Heading, { level: 1, className: "lightbox__title h4", id: "".concat(id, "-title") }, title)), video && /* @__PURE__ */ React.createElement(Video, { ...video, defaultPlaying: true, className: "lightbox__video", onLoad: handleMediaLoad }), image && /* @__PURE__ */ React.createElement(Image, { ...image, className: "lightbox__image", onLoad: handleMediaLoad }))), !hasMediaLoaded && /* @__PURE__ */ React.createElement(Spinner, { className: "lightbox__spinner" }));
};
export {
  Lightbox
};
